import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { RefObject } from 'react'

import type { SnippetImpact } from './types'
import { plural } from './utils'

/** The pre-save check found failures on a shared snippet; asks before saving. */
export function ImpactConfirmDialog({
  impact,
  onSaveAnyway,
  onCancel,
  returnFocusRef,
}: {
  impact: SnippetImpact | null
  onSaveAnyway: () => void
  onCancel: () => void
  /** The Save that opened it; it was disabled during the check, so lost focus. */
  returnFocusRef: RefObject<HTMLElement | null>
}) {
  return (
    <AlertDialog
      open={impact !== null}
      onOpenChange={(open) => {
        if (!open) onCancel()
      }}>
      <AlertDialogContent
        size="sm"
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          returnFocusRef.current?.focus()
        }}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Some duties cannot render this text
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>
                {`${plural(impact?.failing_count ?? 0, 'duty')} would fail on the next call. Save anyway, or cancel and fix the text.`}
              </p>
              <ul className="max-h-48 space-y-1 overflow-y-auto text-sm">
                {impact?.duties
                  .filter((duty) => !duty.ok)
                  .map((duty) => (
                    <li key={`${duty.org_slug ?? ''}:${duty.duty_id}`}>
                      <span className="font-medium">
                        {duty.org_slug
                          ? `${duty.org_slug} · ${duty.duty_name}`
                          : duty.duty_name}
                      </span>
                      {`: ${duty.issues.join('; ')}`}
                    </li>
                  ))}
              </ul>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={onSaveAnyway}>
            Save anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
